<template>
	<component :is="fieldComponent" v-bind="{ ...$attrs, ...characterLimitAttrs }" :rules="mergedRules" ref="fieldRef" type="email">
		<template v-if="$attrs.label && !$slots.label" #label>
			<FieldLabel :label="String($attrs.label)" />
		</template>
		<template v-for="(_, slot) in $slots" #[slot]="scope" :key="slot">
			<slot :name="slot" v-bind="scope || {}"></slot>
		</template>
	</component>
</template>

<script setup lang="ts">
	import { computed, useTemplateRef } from "vue";
	import { VTextField } from "vuetify/components";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { VuetifyValidation } from "@/helpers/Validation";
	import type { CharacterLimitFieldInstance, FieldRule } from "@/composables/useCharacterLimit";
	import CharacterLimitedTextField from "./CharacterLimitedTextField.vue";
	import { EMAIL_MAX_LENGTH, parsePositiveMaxLength } from "@liveswitch-contact/types";

	defineOptions({ inheritAttrs: false });

	const props = withDefaults(
		defineProps<{
			rules?: FieldRule[];
			invalidMessage?: string;
			validateEmail?: boolean;
			maxLength?: number | string;
		}>(),
		{
			rules: () => [],
			invalidMessage: "Please enter a valid email address.",
			validateEmail: true,
			maxLength: EMAIL_MAX_LENGTH
		}
	);

	const fieldRef = useTemplateRef<CharacterLimitFieldInstance>("fieldRef");
	const fieldComponent = computed(() => (props.maxLength === undefined ? VTextField : CharacterLimitedTextField));
	const resolvedEmailMaxLength = computed(() =>
		Math.min(parsePositiveMaxLength(props.maxLength) ?? EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH)
	);
	const characterLimitAttrs = computed(() =>
		props.maxLength === undefined
			? {}
			: {
					maxLength: resolvedEmailMaxLength.value,
					fieldNoun: "email address",
					overLimitMessage: `Email address must not exceed ${resolvedEmailMaxLength.value} characters.`
				}
	);
	const mergedRules = computed<FieldRule[]>(() => {
		if (!props.validateEmail) {
			return props.rules;
		}
		return [
			...props.rules,
			VuetifyValidation.MaxLengthValidationUntrimmed(
				resolvedEmailMaxLength.value,
				`Email address must not exceed ${resolvedEmailMaxLength.value} characters.`
			).isValid,
			VuetifyValidation.EmailValidation(props.invalidMessage).isValid
		];
	});

	async function validate(silent?: boolean): Promise<string[]> {
		return (await fieldRef.value?.validate(silent)) ?? [];
	}

	async function resetValidation(): Promise<void> {
		await fieldRef.value?.resetValidation();
	}

	function focus(): void {
		fieldRef.value?.focus();
	}

	function blur(): void {
		fieldRef.value?.blur();
	}

	defineExpose({ validate, resetValidation, focus, blur });
</script>
